/**
 * Installed-app launch: one inline splash in index.html stays up until the
 * destination surface is ready, then fades out. Pure helpers stay window-free
 * where possible for unit tests.
 */

export const ROAM_LAUNCH_SPLASH_ID = 'roam-app-launch-splash';
export const ROAM_LAUNCH_BG = '#f7f3ef';
export const ROAM_LAUNCH_THEME_COLOR = ROAM_LAUNCH_BG;

export const PWA_STANDALONE_START_PATH = '/trips/active?view=overview';

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  const standaloneMedia =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)').matches
      : false;
  return (
    standaloneMedia ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

export function shouldRedirectStandaloneFromMarketingHome(
  pathname: string,
  search: string,
): boolean {
  return pathname === '/' && search === '';
}

/** Auth screens link home for browser visitors only — not installed PWAs. */
export function shouldShowAuthBackLink(): boolean {
  return !isStandaloneDisplay();
}

/** Home-screen icon / manifest start_url — hold splash through auth + trip load. */
export function isPwaTripLaunchPath(pathname: string): boolean {
  return pathname === '/trips/active';
}

export function isTripLaunchPath(pathname: string): boolean {
  return pathname === '/trips' || pathname.startsWith('/trips/');
}

/**
 * Installed PWAs always hold the splash. Browser tabs hold it only on `/trips*`
 * so the label stays steady, with pointer-events disabled so nothing blocks clicks.
 */
export function shouldHoldLaunchSplash(pathname: string, standalone: boolean): boolean {
  return standalone || isTripLaunchPath(pathname);
}

/** Browser trip loads: keep the inline splash visible but non-interactive. */
export function holdBrowserTripLaunchSplash(): void {
  if (typeof document === 'undefined') return;
  if (isStandaloneDisplay()) return;
  const el = document.getElementById(ROAM_LAUNCH_SPLASH_ID);
  if (!el) return;
  el.style.pointerEvents = 'none';
  document.documentElement.setAttribute('data-roam-hold-splash', '1');
  document.documentElement.setAttribute('data-roam-browser-trip-splash', '1');
}

export function isLaunchSplashHeld(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.hasAttribute('data-roam-hold-splash');
}

export function isLaunchSplashVisible(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.getElementById(ROAM_LAUNCH_SPLASH_ID);
  return Boolean(el && el.dataset.dismissed !== 'true');
}

export function removeAppLaunchSplashImmediate(): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(ROAM_LAUNCH_SPLASH_ID);
  if (!el) {
    document.documentElement.removeAttribute('data-roam-hold-splash');
    return;
  }
  el.remove();
  document.documentElement.removeAttribute('data-roam-hold-splash');
  document.documentElement.removeAttribute('data-roam-browser-trip-splash');
}

export function dismissAppLaunchSplash(): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(ROAM_LAUNCH_SPLASH_ID);
  if (!el || el.dataset.dismissed === 'true') return;
  el.dataset.dismissed = 'true';
  el.style.pointerEvents = 'none';
  el.classList.add('roam-launch-splash--out');
  const remove = () => {
    el.remove();
    document.documentElement.removeAttribute('data-roam-hold-splash');
    document.documentElement.removeAttribute('data-roam-browser-trip-splash');
  };
  el.addEventListener('transitionend', remove, { once: true });
  window.setTimeout(remove, 450);
}

/** Backup if the inline body script did not run (cached HTML, tests, etc.). */
export function installAppLaunchSplashGuards(): void {
  if (typeof document === 'undefined') return;
  const pathname = window.location.pathname;
  if (!shouldHoldLaunchSplash(pathname, isStandaloneDisplay())) {
    removeAppLaunchSplashImmediate();
    return;
  }
  if (!document.getElementById(ROAM_LAUNCH_SPLASH_ID)) return;
  if (isStandaloneDisplay()) {
    document.documentElement.setAttribute('data-roam-hold-splash', '1');
    window.setTimeout(() => dismissAppLaunchSplash(), 12_000);
    return;
  }
  holdBrowserTripLaunchSplash();
}
